import { createTag } from '@frontend/api-client/utils';
import type { BaseApi } from '@frontend/api-client/utils/types';

import type {
  AcceptComplexityPricingRequest,
  CloseTaskRequest,
  CloseTaskResponse,
  ConfirmTaskDoneRequest,
  ConfirmTaskDoneResponse,
  CreateCommentRequest,
  CreateTaskMutation,
  CreateTaskResponse,
  GetShareUrlRequest,
  GetShareUrlResponse,
  GiftTaskRequest,
  GiftTaskResponse,
  ListCommentsResponse,
  ListTasksResponse,
  PreviewTaskResponse,
  RetrieveTaskResponse,
  ShareTaskRequest,
  SnoozeTaskRequest,
  SnoozeTaskResponse,
  StartTaskResponse,
  TaskDetail,
  TriggerSingleChoiceOptionRequest,
  TriggerSingleChoiceOptionResponse,
  UnshareTaskRequest,
  UnsnoozeTaskRequest,
  UnsnoozeTaskResponse,
  UpdateTaskRequest,
  UpdateTaskResponse,
} from '@frontend/api-types';
import { convertArrayObjectKeysCase, convertObjectKeysCase } from '@frontend/duck-tape';
import { TASKS_ENDPOINTS } from './endpoints';
import { transformCommentQuestions } from './utils/taskChat';

export const getTasksApi = (baseApi: BaseApi) => {
  const tasksApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
      acceptComplexityPricing: builder.mutation<void, AcceptComplexityPricingRequest>({
        invalidatesTags: (_result, _error, { taskId }) => [
          createTag({ id: 'LIST', type: 'Task' }),
          createTag({ id: taskId, prefix: 'taskId', type: 'Task' }),
          createTag({ id: taskId, prefix: 'taskId', type: 'Comment' }),
        ],
        query: ({ complexityPricingAgreement, isAccepted, taskId }) => ({
          body: convertObjectKeysCase({ complexityPricingAgreement, isAccepted }, 'snakeCase'),
          method: 'POST',
          url: TASKS_ENDPOINTS.acceptComplexityPricing(taskId),
        }),
      }),
      closeTask: builder.mutation<CloseTaskResponse, CloseTaskRequest>({
        invalidatesTags: (_result, _error, { id: taskId }) => [
          createTag('Client'),
          createTag({ id: 'LIST', type: 'Task' }),
          createTag({ id: taskId, prefix: 'taskId', type: 'Task' }),
          createTag({ id: taskId, prefix: 'taskId', type: 'Comment' }),
        ],
        query: ({ id }) => ({
          method: 'POST',
          url: TASKS_ENDPOINTS.closeTask(id),
        }),
      }),
      confirmDone: builder.mutation<ConfirmTaskDoneResponse, ConfirmTaskDoneRequest>({
        invalidatesTags: (_result, _error, { taskId }) => [
          createTag('Client'),
          createTag({ id: 'LIST', type: 'Task' }),
          createTag({ id: taskId, prefix: 'taskId', type: 'Task' }),
        ],
        query: ({ memberConfirmationStatus, taskId }) => ({
          body: convertObjectKeysCase({ memberConfirmationStatus }, 'snakeCase'),
          method: 'POST',
          url: TASKS_ENDPOINTS.confirmDone(taskId),
        }),
      }),
      createComment: builder.mutation<void, CreateCommentRequest['payload']>({
        invalidatesTags: (_result, _error, { taskId }) => [
          createTag({ id: taskId, prefix: 'taskId', type: 'Comment' }),
          createTag({ id: 'LIST', type: 'Task' }),
        ],
        query: ({ formData: body, taskId }) => ({
          body,
          method: 'POST',
          url: TASKS_ENDPOINTS.createComment(taskId),
        }),
      }),
      createTask: builder.mutation<CreateTaskResponse, CreateTaskMutation['payload']>({
        invalidatesTags: [
          createTag({ id: 'LIST', type: 'Task' }),
          createTag('Client'),
          createTag({ id: 'LIST', type: 'Example' }),
          createTag({ id: 'LIST', type: 'DraftTask' }),
          createTag({ id: 'LIST', type: 'LetterRecommendation' }),
          createTag({ id: 'LIST', type: 'Suggestion' }),
          createTag({ id: 'LIST', type: 'Credit' }),
        ],
        query: ({ formData: body }) => ({
          body,
          method: 'POST',
          url: TASKS_ENDPOINTS.createTask(),
        }),
        transformResponse: (task: GenericRecord) => convertObjectKeysCase(task, 'camelCase') as CreateTaskResponse,
      }),
      // Mutation despite GET because the backend sends email notifications as a side effect
      getShareUrl: builder.mutation<GetShareUrlResponse, GetShareUrlRequest>({
        query: ({ emails, taskId }) => {
          const params = new URLSearchParams();
          emails.forEach((email) => params.append('emails', email));
          return {
            method: 'GET',
            url: `${TASKS_ENDPOINTS.getShareUrl(taskId)}${emails.length > 0 ? `?${params.toString()}` : ''}`,
          };
        },
        transformResponse: (response: GenericRecord) =>
          convertObjectKeysCase(response, 'camelCase') as GetShareUrlResponse,
      }),
      giftTask: builder.mutation<GiftTaskResponse, GiftTaskRequest>({
        query: (body) => ({
          body: convertObjectKeysCase(body, 'snakeCase'),
          method: 'POST',
          url: TASKS_ENDPOINTS.giftTask(),
        }),
        transformResponse: (response: GenericRecord) =>
          convertObjectKeysCase(response, 'camelCase') as GiftTaskResponse,
      }),
      listComments: builder.query<ListCommentsResponse, { format?: 'html' | 'markdown'; id: string }>({
        providesTags: (_result, _error, { id }) => [createTag({ id, prefix: 'taskId', type: 'Comment' })],
        query: ({ format = 'html', id }) => ({
          params: format === 'markdown' ? { html: false } : undefined,
          url: TASKS_ENDPOINTS.listComments(id),
        }),
        transformResponse: ({ comments }: { comments: GenericRecord[] }) => {
          const camelCaseComments = convertArrayObjectKeysCase(comments, 'camelCase') as ListCommentsResponse;
          return transformCommentQuestions(camelCaseComments);
        },
      }),
      listTasks: builder.query<ListTasksResponse, void>({
        providesTags: [createTag({ id: 'LIST', type: 'Task' })],
        query: TASKS_ENDPOINTS.listTasks,
        transformResponse: (response: GenericRecord[]) =>
          convertArrayObjectKeysCase(response, 'camelCase') as ListTasksResponse,
      }),
      previewTask: builder.query<PreviewTaskResponse, string>({
        query: TASKS_ENDPOINTS.previewTask,
        transformResponse: (response: GenericRecord) =>
          convertObjectKeysCase(response, 'camelCase') as PreviewTaskResponse,
      }),
      retrieveTask: builder.query<RetrieveTaskResponse, string>({
        providesTags: (_result, _error, taskId) => [createTag({ id: taskId, prefix: 'taskId', type: 'Task' })],
        query: TASKS_ENDPOINTS.retrieveTask,
        transformResponse: (response: GenericRecord) =>
          convertObjectKeysCase(response, 'camelCase') as RetrieveTaskResponse,
      }),
      shareTask: builder.mutation<void, ShareTaskRequest>({
        invalidatesTags: (_result, _error, { id }) => [createTag({ id, prefix: 'taskId', type: 'Task' })],
        query: ({ clientId, id }) => ({
          method: 'POST',
          url: TASKS_ENDPOINTS.accessTask(id, clientId),
        }),
      }),
      skipTask: builder.mutation<TaskDetail, string>({
        invalidatesTags: (_result, _error) => [createTag({ id: 'LIST', type: 'Task' })],
        onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
          try {
            const { data: updatedTask } = await queryFulfilled;
            dispatch(
              tasksApi.util.updateQueryData('retrieveTask', id, (draft) => {
                return { ...draft, ...updatedTask };
              }),
            );
          } catch {
            /* If there is an error, don't update the cache */
          }
        },
        query: (id) => ({
          method: 'POST',
          url: TASKS_ENDPOINTS.skipTask(id),
        }),
        transformResponse: (response: GenericRecord) => convertObjectKeysCase(response, 'camelCase') as TaskDetail,
      }),
      snoozeTask: builder.mutation<SnoozeTaskResponse, SnoozeTaskRequest>({
        invalidatesTags: (_result, _error, { taskId }) => [
          createTag({ id: 'LIST', type: 'Task' }),
          createTag({ id: taskId, prefix: 'taskId', type: 'Task' }),
          createTag({ id: taskId, prefix: 'taskId', type: 'Comment' }),
        ],
        query: ({ snoozeDate, taskId }) => ({
          body: convertObjectKeysCase({ snoozeDate }, 'snakeCase'),
          method: 'POST',
          url: TASKS_ENDPOINTS.snoozeTask(taskId),
        }),
      }),
      startTask: builder.mutation<StartTaskResponse, string>({
        invalidatesTags: (_result, _error) => [createTag({ id: 'LIST', type: 'Task' })],
        onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
          try {
            const { data: updatedTask } = await queryFulfilled;
            dispatch(
              tasksApi.util.updateQueryData('retrieveTask', id, (draft) => {
                return { ...draft, ...updatedTask };
              }),
            );
          } catch {
            /* If there is an error, don't update the cache */
          }
        },
        query: (id) => ({
          method: 'POST',
          url: TASKS_ENDPOINTS.startTask(id),
        }),
        transformResponse: (task: GenericRecord) => convertObjectKeysCase(task, 'camelCase') as StartTaskResponse,
      }),
      triggerSingleChoiceOption: builder.mutation<TriggerSingleChoiceOptionResponse, TriggerSingleChoiceOptionRequest>({
        invalidatesTags: (_result, _error, { taskId }) => [
          createTag({ id: taskId, prefix: 'taskId', type: 'Comment' }),
          createTag({ id: taskId, prefix: 'taskId', type: 'Task' }),
        ],
        query: ({ commentId, optionId, selectedOption, taskId }) => ({
          body: convertObjectKeysCase({ commentId, optionId, selectedOption, taskId }, 'snakeCase'),
          method: 'POST',
          url: TASKS_ENDPOINTS.triggerSingleChoiceOption(taskId),
        }),
      }),
      unshareTask: builder.mutation<void, UnshareTaskRequest>({
        invalidatesTags: (_result, _error, { id }) => [createTag({ id, prefix: 'taskId', type: 'Task' })],
        query: ({ clientId, id }) => ({
          method: 'DELETE',
          url: TASKS_ENDPOINTS.accessTask(id, clientId),
        }),
      }),
      unsnoozeTask: builder.mutation<UnsnoozeTaskResponse, UnsnoozeTaskRequest>({
        invalidatesTags: (_result, _error, { taskId }) => [
          createTag({ id: 'LIST', type: 'Task' }),
          createTag({ id: taskId, prefix: 'taskId', type: 'Task' }),
          createTag({ id: taskId, prefix: 'taskId', type: 'Comment' }),
        ],
        query: ({ taskId }) => ({
          body: { id: taskId },
          method: 'POST',
          url: TASKS_ENDPOINTS.unsnoozeTask(taskId),
        }),
      }),
      updateTask: builder.mutation<UpdateTaskResponse, UpdateTaskRequest>({
        invalidatesTags: (_result, _error, { id }) => [
          createTag({ id: 'LIST', type: 'Task' }),
          createTag({ id, prefix: 'taskId', type: 'Task' }),
        ],
        query: ({ data, id }) => ({
          body: convertObjectKeysCase(data, 'snakeCase'),
          method: 'PATCH',
          url: TASKS_ENDPOINTS.updateTask(id),
        }),
        transformResponse: (task: GenericRecord) => convertObjectKeysCase(task, 'camelCase') as UpdateTaskResponse,
      }),
    }),
    overrideExisting: true,
  });

  return tasksApi;
};
